const post = {
  "slug": "connect-instagram-business-account-checkout-dm",
  "title": "How to Connect Your Instagram Account to Checkout DM (and Fix Errors)",
  "seoTitle": "Connect Instagram to Checkout DM: Fix Errors",
  "description": "How the Instagram connection works, why a Business account matters and what to do if you see \u2019We couldn\u2019t connect\u2019 or your account shows as disconnected.",
  "excerpt": "Connecting takes a few minutes, but a cancelled login or the wrong account type can stop it. Here is how it works and how to fix each common error.",
  "category": "Product",
  "tags": [
    "Connect Instagram",
    "Instagram Business account",
    "Checkout DM setup",
    "Instagram connection error"
  ],
  "date": "2026-11-27",
  "cover": {
    "src": "/blog-images/connect-instagram-business-account-checkout-dm-cover.png",
    "alt": "An Instagram account connecting to Checkout DM through the official login"
  },
  "blocks": [
    {
      "type": "p",
      "text": "To sell through Instagram DMs, Checkout DM has to be allowed to read and reply to the messages on your account. You grant that once, through Instagram\u2019s own login. Most connections take a few minutes and work first time, and the Checkout DM site says a store can be live in about five minutes. This guide explains how the connection works, what account type you need and how to fix the two screens people most often run into."
    },
    {
      "type": "h2",
      "text": "How the connection works"
    },
    {
      "type": "ol",
      "items": [
        "**Choose Connect Instagram** on the Checkout DM site.",
        "**Sign in on Instagram\u2019s own page** and approve the permissions it lists. Your Instagram password is never shared with Checkout DM.",
        "**Land on the connected screen.** You see a message that your Instagram is now linked, and your first checkout link arrives inside your DMs shortly after."
      ]
    },
    {
      "type": "p",
      "text": "Because the login happens on Instagram\u2019s page, you stay in control of what you approve, and you can remove the connection at any time from your Instagram or Facebook settings. If you want to understand what the permissions mean, read [how Checkout DM uses Meta\u2019s official OAuth](/blog/is-checkout-dm-safe-instagram-oauth). If a tool ever asks for your Instagram password, do not use it."
    },
    {
      "type": "h2",
      "text": "The account type you need"
    },
    {
      "type": "p",
      "text": "Tools that automate your messages work with a professional Instagram account, not a personal one. Checkout DM\u2019s connection screens refer to an Instagram Business account. If you are not sure what type your account is, check it in Instagram\u2019s settings, and switch to a professional account there before you connect. If the connection fails and you are on a Creator account, switching to Business is the first thing to try."
    },
    {
      "type": "h2",
      "text": "If you see \"We couldn\u2019t connect your Instagram account\""
    },
    {
      "type": "p",
      "text": "This screen means the connection was not completed. The page lists the usual reasons:"
    },
    {
      "type": "ul",
      "items": [
        "**The authorization was cancelled.** Someone closed the Instagram login or chose not to approve. Try again and approve the permissions.",
        "**The authorization expired before it was completed.** Start again and finish the steps in one go.",
        "**The selected account is not an Instagram Business account.** Switch the account to a professional account on Instagram, then reconnect.",
        "**A temporary network or Meta service issue.** Wait a few minutes and try again."
      ]
    },
    {
      "type": "p",
      "text": "Choose Try Again to restart the connection. If the problem continues, disconnect Checkout DM from Instagram and then connect again."
    },
    {
      "type": "h2",
      "text": "If your account shows as disconnected"
    },
    {
      "type": "p",
      "text": "A different screen appears when Checkout DM no longer has permission to access your Instagram account. Automation is paused until you reconnect, and the page lists the usual causes: you removed Checkout DM from your Instagram or Facebook business integrations, required permissions were revoked or your authorization expired and needs renewing."
    },
    {
      "type": "p",
      "text": "The reassuring part is that your customers, products, analytics and subscription remain safe. Choose Reconnect Instagram, and after you do, new messages and automations continue normally and your existing data is untouched."
    },
    {
      "type": "callout",
      "tone": "mint",
      "title": "After you connect",
      "text": "Add your products, choose how you want to be paid and send yourself a test order from a second Instagram account. Our guide to [automating sales on Instagram](/blog/how-to-automate-sales-on-instagram) walks through the whole setup in order."
    },
    {
      "type": "h2",
      "text": "What your customers need"
    },
    {
      "type": "p",
      "text": "Nothing. Customers do not install an app or create a Checkout DM account. They message you on Instagram as they always have, and they pay you directly, by UPI, card or wallet, or by the manual method you chose. Checkout DM never charges the buyer. That is why the connection is the only setup step on your side: once your account is linked and your catalog is ready, customers can start ordering from the DMs they already use."
    },
    {
      "type": "h2",
      "text": "Choosing a plan after you connect"
    },
    {
      "type": "p",
      "text": "The connected screen offers a Choose a plan button, but you can begin on the Free plan, which includes 5 products and 15 customer conversations a month and needs no card. Starter and above remove the conversation limit. Paid plans start at \u20b9499 a month, and you can change plans at any time without losing your products, orders or customer history. Our [pricing guide](/blog/checkout-dm-pricing-and-plans-explained) lists what each plan includes."
    },
    {
      "type": "h2",
      "text": "Is it allowed?"
    },
    {
      "type": "p",
      "text": "Checkout DM connects through Meta\u2019s official login rather than any workaround, which is what makes a connection safe for your account. For the wider question of what Instagram allows from automation tools, read [whether Instagram DM automation is safe](/blog/is-instagram-dm-automation-safe-2026)."
    },
    {
      "type": "h2",
      "text": "What to do next"
    },
    {
      "type": "ul",
      "items": [
        "**Add products.** See [Instagram catalog tips](/blog/instagram-catalog-tips-photos-sizes) for photos, names and sizes.",
        "**Choose payments.** Manual UPI works from the Free plan, and our guide to [connecting Razorpay](/blog/connect-razorpay-to-checkout-dm) covers automatic confirmation.",
        "**Pick your plan.** The Free plan includes 5 products and 15 conversations a month, with no card needed."
      ]
    }
  ],
  "related": [
    {
      "to": "/help",
      "title": "Help Guide",
      "description": "Setup steps and how the pieces fit together."
    },
    {
      "to": "/pricing",
      "title": "Pricing",
      "description": "Start free, upgrade as you grow."
    },
    {
      "to": "/catalog-guide",
      "title": "Catalog Guide",
      "description": "Add and manage the products your bot sells."
    }
  ],
  "faqs": [
    {
      "q": "Does Checkout DM ever see my Instagram password?",
      "a": "No. You sign in on Instagram\u2019s own page and approve a list of permissions. Your password is never shared with Checkout DM."
    },
    {
      "q": "Why does it say my account is not an Instagram Business account?",
      "a": "The connection needs a professional account. Switch your account to a professional account in Instagram\u2019s settings, then connect again."
    },
    {
      "q": "What should I do if the connection fails?",
      "a": "Choose Try Again. If the problem continues, disconnect Checkout DM from Instagram and then connect again."
    },
    {
      "q": "My account shows as disconnected. Did I lose my data?",
      "a": "No. Your customers, products, analytics and subscription remain safe. Choose Reconnect Instagram and your automations continue normally."
    },
    {
      "q": "How long does setup take?",
      "a": "The Checkout DM site says a store can be live in about five minutes: connect your Instagram account, add products and choose how you want to be paid."
    },
    {
      "q": "Do my customers need an account or an app?",
      "a": "No. Customers message you on Instagram as usual, with nothing to install and no Checkout DM account to create. Checkout DM never charges the buyer."
    },
    {
      "q": "Why would Checkout DM become disconnected?",
      "a": "Common causes are removing Checkout DM from Instagram or Facebook business integrations, revoked permissions or an authorization that expired."
    }
  ]
};

export default post;
